/**
 * PipelineDonut.tsx — the Sales Home pipeline ring (redesign slice 3): the
 * four open stages as arcs around the open count, a legend row per stage
 * (zero rows included — a stage does not vanish when empty) that DRILLS to
 * the stage's deal list exactly as the stage rows it replaces did (SB-12),
 * and the all-time closed-won count beneath, drilling likewise. The model
 * and the fill binding are pipelineDonutView.ts / funnelRows.ts.
 */
import { donutModel, RING, type DonutModel } from './pipelineDonutView'
import type { FunnelCount } from './funnelRows'

export function PipelineDonut({ funnel, onDrill }: {
  funnel: FunnelCount[]
  onDrill: (row: { stage: string; label: string; count: number }) => void
}) {
  const m: DonutModel = donutModel(funnel)
  const c = RING.box / 2
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-5">
        <div className="relative flex-shrink-0" style={{ width: RING.box, height: RING.box }} data-chart="pipeline-donut">
          <svg width={RING.box} height={RING.box} viewBox={`0 0 ${RING.box} ${RING.box}`} role="img" aria-label={`${m.openTotal} open deals by stage`}>
            {m.arcs.length === 0 && (
              <circle cx={c} cy={c} r={RING.radius} fill="none" stroke="var(--panel2)" strokeWidth={RING.stroke} />
            )}
            {m.arcs.map(a => (
              <circle key={a.stage} cx={c} cy={c} r={RING.radius} fill="none" stroke={a.fill} strokeWidth={RING.stroke}
                strokeDasharray={a.dashArray} strokeDashoffset={a.dashOffset} transform={`rotate(-90 ${c} ${c})`} data-stage={a.stage}>
                <title>{a.title}</title>
              </circle>
            ))}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-ink-primary font-semibold" style={{ fontSize: 26, letterSpacing: '-0.02em', lineHeight: 1 }}>{m.openTotal}</span>
            <span className="text-ink-secondary" style={{ fontSize: 11 }}>open deals</span>
          </div>
        </div>
        <div className="flex flex-col gap-[10px] flex-1 min-w-0 text-13">
          {m.legend.map(r => (
            <button key={r.stage} type="button" data-stage={r.stage} onClick={() => onDrill({ stage: r.stage, label: r.label, count: r.count })}
              className="flex items-center gap-2 w-full text-left cursor-pointer bg-transparent border-0 p-0 rounded-control hover:bg-surface-nav-hover focus:outline-none">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.fill }} aria-hidden="true" />
              <span className="flex-1 text-ink-body">{r.label}</span>{' '}
              <span className="font-semibold text-ink-primary">{r.count}</span>
            </button>
          ))}
        </div>
      </div>
      {m.won && (
        <button type="button" data-stage={m.won.stage} onClick={() => onDrill({ stage: m.won!.stage, label: m.won!.label, count: m.won!.count })}
          className="flex items-center justify-between w-full text-left cursor-pointer bg-transparent border-0 p-0 pt-3 text-13 rounded-control hover:bg-surface-nav-hover focus:outline-none"
          style={{ borderTop: '1px solid var(--hair)' }}>
          <span className="text-ink-secondary">Closed won, all-time</span>{' '}
          <span className="font-semibold text-ink-primary">{m.won.count} {m.won.count === 1 ? 'deal' : 'deals'}</span>
        </button>
      )}
    </div>
  )
}
